import { Tag, TagProps } from 'antd';
import React from 'react';
import { BLUE, DANGER, GREEN, GREY, YELLOW } from '../Theme/palette';

const GROUPS = { blue: BLUE, green: GREEN, yellow: YELLOW, danger: DANGER, grey: GREY };

export type SoftTagGroup = keyof typeof GROUPS;

interface IProps extends Omit<TagProps, 'color' | 'variant'> {
  group: SoftTagGroup;
}

/**
 * The one chip look for a label: the palette group's soft fill, its strong colour for the text, its
 * pale colour for the outline. Used for facts and statuses alike ("App user", "Labour", "Paid") -
 * the stock antd colour presets (`color="green"`) are derived from the palette but come out muddy
 * next to it, so they are not used. Calendar cards carry the solid version (CalendarStatusStyle).
 */
export const SoftTag: React.FC<IProps> = ({ group, style, children, ...rest }) => {
  const colours = GROUPS[group];
  return (
    <Tag
      variant="filled"
      {...rest}
      style={{
        margin: 0,
        color: colours.tag,
        background: colours.soft,
        border: `1px solid ${colours.card}`,
        ...style,
      }}
    >
      {children}
    </Tag>
  );
};

const PRESET_GROUP: Record<string, SoftTagGroup> = {
  green: 'green', success: 'green',
  red: 'danger', error: 'danger',
  blue: 'blue', processing: 'blue', geekblue: 'blue', cyan: 'blue', purple: 'blue',
  orange: 'yellow', gold: 'yellow', warning: 'yellow', volcano: 'yellow',
  default: 'grey',
};

/**
 * For a tag whose colour comes from data (a status map, a condition) and was written as an antd
 * preset name: the palette's version of that name. An unknown or missing colour is a plain tag.
 */
export const PresetTag: React.FC<Omit<TagProps, 'variant'>> = ({ color, children, ...rest }) => {
  const group = color ? PRESET_GROUP[color] : undefined;
  return group ? (
    <SoftTag group={group} {...rest}>
      {children}
    </SoftTag>
  ) : (
    <Tag {...rest} color={color}>
      {children}
    </Tag>
  );
};
